import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import Spinner from '../components/ui/Spinner';
import SEO from '../components/seo/SEO';
import { useRegionalSeo } from '../hooks/useRegionalSeo';
import { useSeoExtras } from '../context/SiteSeoContext';
import { fetchAuthors } from '../store/actions/authorActions';
import { transformAuthors } from '../utils/transformData';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const PageHeader = styled.div`
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  max-width: 42rem;
  line-height: 1.7;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const AuthorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 3rem;
  }
`;

const AuthorCard = styled(Link)`
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: 2rem;
  text-align: center;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s;
  
  @media (min-width: 1024px) {
    padding: 2.5rem;
  }
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.medium};
    transform: translateY(-0.25rem);
  }
`;

const AuthorAvatar = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 9999px;
  background-color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  
  @media (min-width: 1024px) {
    width: 5rem;
    height: 5rem;
    margin-bottom: 1.25rem;
  }
`;

const AvatarLetter = styled.span`
  color: ${props => props.theme.colors.primaryForeground};
  font-family: ${props => props.theme.typography.fonts.display};
  font-weight: ${props => props.theme.typography.fontWeights.bold};
  font-size: 1.5rem;
  
  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`;

const AuthorName = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.25rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.5rem;
  
  @media (min-width: 1024px) {
    font-size: 1.375rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.5rem;
  }
`;

const AuthorStats = styled.div`
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.mutedForeground};
  
  @media (min-width: 1024px) {
    font-size: 1rem;
  }
`;

const Authors = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language, apiRegion, getAuthorUrl } = useLanguage();
  // Use language from context (set based on region from URL)
  const activeLanguage = language;

  const { authors: authorsFromStore, loadingProfile: loading } = useSelector(state => state.author);

  const regionalSeo = useRegionalSeo('/authors');
  const seoExtras = useSeoExtras();

  useEffect(() => {
    dispatch(fetchAuthors({ lang: activeLanguage, region: apiRegion }));
  }, [dispatch, activeLanguage, apiRegion]);

  const authors = transformAuthors(authorsFromStore);

  if (loading) {
    return (
      <PageWrapper>
        <Header />
        <Main>
          <Container>
            <Spinner />
          </Container>
        </Main>
        <Footer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO
        title="Our Authors"
        description="Meet the talented writers who share their insights, stories, and expertise with our community."
        url={regionalSeo.pageUrl}
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
        {...seoExtras}
      />
      <Header />
      <Main>
        <Container>
          <PageHeader>
            <PageTitle>Our Authors</PageTitle>
            <PageDescription>
              Meet the talented writers who share their insights, stories, and expertise with our community.
            </PageDescription>
          </PageHeader>

          <AuthorsGrid>
            {authors.map((author) => (
              <AuthorCard key={author.slug || author.name} to={getAuthorUrl(author.slug || encodeURIComponent(author.name), activeLanguage)}>
                <AuthorAvatar>
                  {author.avatar ? (
                    <img src={author.avatar} alt={author.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <AvatarLetter>{author.name.charAt(0)}</AvatarLetter>
                  )}
                </AuthorAvatar>
                <AuthorName>{author.name}</AuthorName>
                <AuthorStats>
                  {t('author.articlesCount', { count: author.articleCount || 0 })}
                </AuthorStats>
              </AuthorCard>
            ))}
          </AuthorsGrid>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Authors;

